import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { filterOptions } from "../mockData";

const durationOptions = [
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_60_days", label: "Last 60 Days" },
  { value: "last_90_days", label: "Last 90 Days" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "ytd", label: "Year to Date (YTD)" },
];

const PATIENT_CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "synced", label: "Synced Patient" },
  { value: "precise", label: "Precise Only" },
  { value: "healthie", label: "Healthie Only" },
];

const calculateDateRange = (duration) => {
  const now = new Date();
  let startDate, endDate;

  endDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  );

  switch (duration) {
    case "last_30_days": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      startDate = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      );
      break;
    }
    case "last_60_days": {
      const d = new Date(now);
      d.setDate(d.getDate() - 60);
      startDate = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      );
      break;
    }
    case "last_90_days": {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      startDate = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      );
      break;
    }
    case "last_6_months": {
      const d = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      startDate = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      );
      break;
    }
    case "ytd":
      startDate = new Date(Date.UTC(now.getFullYear(), 0, 1, 0, 0, 0, 0));
      break;
    default:
      return null;
  }

  return {
    start: startDate.toISOString(),
    end: endDate.toISOString(),
  };
};

const getLocalDateFromUTC = (utcDateString) => {
  const utcDate = new Date(utcDateString);
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate()
  );
};

const getUTCDateRange = (dates) => {
  if (!Array.isArray(dates) || dates.length !== 2) return null;
  const start = new Date(dates[0]);
  const end = new Date(dates[1]);
  const startUTC = new Date(
    Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0)
  );
  const endUTC = new Date(
    Date.UTC(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
      23,
      59,
      59,
      999
    )
  );
  return {
    start: startUTC.toISOString(),
    end: endUTC.toISOString(),
  };
};

const DEFAULT_DURATION = "";

const buildInitialFilters = () => {
  const range = calculateDateRange(DEFAULT_DURATION);
  return {
    clinicIds: [],
    patientIds: [],
    assessmentTypes: [],
    genders: [],
    ageRange: "",
    ticDuration: "",
    ticStartDate: "",
    ticEndDate: "",
    duration: DEFAULT_DURATION,
    startDate: range ? range.start : "",
    endDate: range ? range.end : "",
  };
};

const assessmentTypeOptions = filterOptions.assessmentTypes
  .filter((opt) => opt.value)
  .map((opt) => ({ value: opt.value, label: opt.label }));

const genderOptions = filterOptions.genders
  .filter((opt) => opt.value)
  .map((opt) => ({ value: opt.value, label: opt.label }));

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="analytics-calendar-icon"
  >
    <path
      d="M12.667 2.667H3.333C2.597 2.667 2 3.264 2 4v9.333c0 .737.597 1.334 1.333 1.334h9.334c.736 0 1.333-.597 1.333-1.334V4c0-.736-.597-1.333-1.333-1.333zM10.667 1.333v2.667M5.333 1.333v2.667M2 6.667h12"
      stroke="#94A3B8"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AnalyticsFilters = ({
  activeTab,
  onApply,
  onReset,
  clinicList,
  clinicListLoading,
  patientList,
  patientListLoading,
  onClinicChange,
  isDataLoading,
  loadingAction,
}) => {
  const isPatientLevel = activeTab === "patient";
  const [filters, setFilters] = useState(buildInitialFilters);
  const [dateRangeType, setDateRangeType] = useState("duration");
  const [timeInCareType, setTimeInCareType] = useState("duration");
  const [ticFlatpickrKey, setTicFlatpickrKey] = useState(0);
  const [patientCategory, setPatientCategory] = useState("");

  const hasGroupedPatientShape =
    patientList?.mapped_patients !== undefined ||
    patientList?.unmapped_patients !== undefined;

  const clinicOptions = useMemo(() => {
    if (!clinicList) return [];
    const list =
      clinicList?.clinics ||
      clinicList?.results ||
      (Array.isArray(clinicList) ? clinicList : []);
    if (!Array.isArray(list)) return [];
    return list.map((c) => ({
      value: c.id || c.clinic_id,
      label: c.name || c.clinic_name || `Clinic ${c.id}`,
    }));
  }, [clinicList]);

  const patientOptions = useMemo(() => {
    if (!patientList) return [];

    const toOption = (p) => ({
      value: {
        type: p.type,
        patient_id: p.patient_id || null,
        healthie_patient_id: p.healthie_patient_id || null,
      },
      label:
        p.full_name ||
        p.name ||
        p.patient_name ||
        [p.first_name, p.last_name].filter(Boolean).join(" ") ||
        `Patient ${p.patient_id || p.healthie_patient_id || p.id}`,
    });

    if (hasGroupedPatientShape) {
      const mapped = Array.isArray(patientList?.mapped_patients)
        ? patientList.mapped_patients
        : [];
      const unmappedPrecise = Array.isArray(
        patientList?.unmapped_patients?.precise_patients
      )
        ? patientList.unmapped_patients.precise_patients
        : [];
      const unmappedHealthie = Array.isArray(
        patientList?.unmapped_patients?.healthie_patients
      )
        ? patientList.unmapped_patients.healthie_patients
        : [];
      const groups = [];
      if (mapped.length) {
        groups.push({
          label: "Synced Patient",
          options: mapped.map(toOption),
        });
      }
      if (unmappedPrecise.length) {
        groups.push({
          label: "Precise Only",
          options: unmappedPrecise.map(toOption),
        });
      }
      if (unmappedHealthie.length) {
        groups.push({
          label: "Healthie Only",
          options: unmappedHealthie.map(toOption),
        });
      }
      return groups;
    }

    const list =
      patientList?.patients ||
      patientList?.results ||
      (Array.isArray(patientList) ? patientList : []);
    if (!Array.isArray(list)) return [];
    return list.map(toOption);
  }, [patientList, hasGroupedPatientShape]);

  const flatPatientOptions = useMemo(() => {
    return patientOptions.flatMap((opt) =>
      Array.isArray(opt?.options) ? opt.options : [opt]
    );
  }, [patientOptions]);

  const visiblePatientOptions = useMemo(() => {
    if (!hasGroupedPatientShape) return patientOptions;
    if (!patientCategory) return [];
    if (patientCategory === "all") return patientOptions;
    const groupLabel = PATIENT_CATEGORY_OPTIONS.find(
      (opt) => opt.value === patientCategory
    )?.label;
    const group = patientOptions.find((opt) => opt.label === groupLabel);
    return Array.isArray(group?.options) ? group.options : [];
  }, [hasGroupedPatientShape, patientCategory, patientOptions]);

  const [flatpickrKey, setFlatpickrKey] = useState(0);

  useEffect(() => {
    if (filters.duration) {
      setDateRangeType("duration");
    } else if (filters.startDate && filters.endDate) {
      setDateRangeType("custom");
    }
  }, [filters.duration, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (isPatientLevel) {
      setFilters((prev) => ({
        ...prev,
        assessmentTypes: [],
      }));
    }
  }, [isPatientLevel]);

  useEffect(() => {
    const validGenderSet = new Set(genderOptions.map((opt) => opt.value));
    setFilters((prev) => {
      const sanitizedGenders = (prev.genders || []).filter((gender) =>
        validGenderSet.has(gender)
      );
      if (sanitizedGenders.length === (prev.genders || []).length) {
        return prev;
      }
      return { ...prev, genders: sanitizedGenders };
    });
  }, []);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiChange = (field, selected) => {
    const values = selected ? selected.map((s) => s.value) : [];
    setFilters((prev) => {
      const next = { ...prev, [field]: values };
      if (field === "clinicIds" && values.length === 0) {
        next.patientIds = [];
      }
      return next;
    });
    if (field === "clinicIds" && onClinicChange) {
      onClinicChange(values);
    }
  };

  const handleDurationChange = (duration) => {
    if (duration) {
      const range = calculateDateRange(duration);
      setFilters((prev) => ({
        ...prev,
        duration,
        startDate: range ? range.start : "",
        endDate: range ? range.end : "",
      }));
      setFlatpickrKey((prev) => prev + 1);
    } else {
      setFilters((prev) => ({ ...prev, duration: "" }));
    }
  };

  const handleDateRangeTypeChange = (type) => {
    if (type === dateRangeType) return;
    setDateRangeType(type);
    setFilters((prev) => ({
      ...prev,
      duration: "",
      startDate: "",
      endDate: "",
    }));
    setFlatpickrKey((prev) => prev + 1);
  };

  const handleCustomDateChange = (dates) => {
    if (dates.length === 2) {
      const range = getUTCDateRange(dates);
      setFilters((prev) => ({
        ...prev,
        duration: "",
        startDate: range.start,
        endDate: range.end,
      }));
      setDateRangeType("custom");
    }
  };

  const handleFlatpickrClose = (dates) => {
    if (dates.length < 2) {
      setFilters((prev) => ({ ...prev, startDate: "", endDate: "" }));
    }
  };

  const handleTimeInCareTypeChange = (type) => {
    if (type === timeInCareType) return;
    setTimeInCareType(type);
    setFilters((prev) => ({
      ...prev,
      ticDuration: "",
      ticStartDate: "",
      ticEndDate: "",
    }));
    setTicFlatpickrKey((prev) => prev + 1);
  };

  const handleTicDurationChange = (duration) => {
    if (duration) {
      const range = calculateDateRange(duration);
      setFilters((prev) => ({
        ...prev,
        ticDuration: duration,
        ticStartDate: range ? range.start : "",
        ticEndDate: range ? range.end : "",
      }));
      setTicFlatpickrKey((prev) => prev + 1);
    } else {
      setFilters((prev) => ({
        ...prev,
        ticDuration: "",
        ticStartDate: "",
        ticEndDate: "",
      }));
    }
  };

  const handleTicCustomDateChange = (dates) => {
    if (dates.length === 2) {
      const range = getUTCDateRange(dates);
      setFilters((prev) => ({
        ...prev,
        ticDuration: "",
        ticStartDate: range.start,
        ticEndDate: range.end,
      }));
      setTimeInCareType("custom");
    }
  };

  const handleTicFlatpickrClose = (dates) => {
    if (dates.length < 2) {
      setFilters((prev) => ({
        ...prev,
        ticStartDate: "",
        ticEndDate: "",
      }));
    }
  };

  const handleReset = () => {
    const resetFilters = buildInitialFilters();
    setFilters(resetFilters);
    setDateRangeType("duration");
    setFlatpickrKey((prev) => prev + 1);
    setTimeInCareType("duration");
    setTicFlatpickrKey((prev) => prev + 1);
    setPatientCategory("");
    onReset?.(resetFilters);
  };

  const handleApply = () => {
    const validGenderSet = new Set(genderOptions.map((opt) => opt.value));
    const sanitizedGenders = (filters.genders || []).filter((gender) =>
      validGenderSet.has(gender)
    );
    const nextFilters = { ...filters, genders: sanitizedGenders };
    if (sanitizedGenders.length !== (filters.genders || []).length) {
      setFilters(nextFilters);
    }
    onApply?.(nextFilters);
  };

  const renderDateRangeModeRadios = (name, value, onChange) => (
    <div className="analytics-date-range-radios">
      <label className="analytics-radio-label">
        <input
          type="radio"
          name={name}
          checked={value === "duration"}
          onChange={() => onChange("duration")}
        />
        <span className="analytics-radio-mark" />
        Duration
      </label>
      <label className="analytics-radio-label">
        <input
          type="radio"
          name={name}
          checked={value === "custom"}
          onChange={() => onChange("custom")}
        />
        <span className="analytics-radio-mark" />
        Custom
      </label>
    </div>
  );

  const renderPatientCategoryRadios = () => (
    <div className="analytics-date-range-radios analytics-patient-category-radios">
      {PATIENT_CATEGORY_OPTIONS.map((opt) => (
        <label key={opt.value} className="analytics-radio-label">
          <input
            type="radio"
            name="patientCategory"
            checked={patientCategory === opt.value}
            onChange={() => setPatientCategory(opt.value)}
          />
          <span className="analytics-radio-mark" />
          {opt.label}
        </label>
      ))}
    </div>
  );

  const renderAgeRangeField = () => (
    <div className="analytics-filter-field analytics-filter-field--age-range">
      <label>Age Range</label>
      <select
        value={filters.ageRange}
        onChange={(e) => handleChange("ageRange", e.target.value)}
        className={!filters.ageRange ? "placeholder-shown" : ""}
      >
        {filterOptions.ageRanges.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );

  const renderTimeInCareField = () => (
    <div className="analytics-filter-field analytics-filter-field--time-in-care">
      <label>Time in care</label>
      {renderDateRangeModeRadios(
        "timeInCareType",
        timeInCareType,
        handleTimeInCareTypeChange
      )}
      {timeInCareType === "duration" ? (
        <select
          value={filters.ticDuration}
          onChange={(e) => handleTicDurationChange(e.target.value)}
          className={!filters.ticDuration ? "placeholder-shown" : ""}
        >
          <option value="">All Time in Care</option>
          {durationOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="analytics-select-wrapper">
          <Flatpickr
            key={ticFlatpickrKey}
            className="analytics-flatpickr-input"
            value={
              filters.ticStartDate && filters.ticEndDate
                ? [
                    getLocalDateFromUTC(filters.ticStartDate),
                    getLocalDateFromUTC(filters.ticEndDate),
                  ]
                : []
            }
            options={{
              mode: "range",
              dateFormat: "m-d-Y",
            }}
            placeholder="Select custom date range"
            onChange={handleTicCustomDateChange}
            onClose={handleTicFlatpickrClose}
          />
          <CalendarIcon />
        </div>
      )}
    </div>
  );

  const renderTimeRangeField = () => (
    <div className="analytics-filter-field analytics-filter-field--date-range">
      <label>Time Range</label>
      {renderDateRangeModeRadios(
        "dateRangeType",
        dateRangeType,
        handleDateRangeTypeChange
      )}
      {dateRangeType === "duration" ? (
        <select
          value={filters.duration}
          onChange={(e) => handleDurationChange(e.target.value)}
          className={!filters.duration ? "placeholder-shown" : ""}
        >
          <option value="">All Durations</option>
          {durationOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="analytics-select-wrapper">
          <Flatpickr
            key={flatpickrKey}
            className="analytics-flatpickr-input"
            value={
              filters.startDate && filters.endDate
                ? [
                    getLocalDateFromUTC(filters.startDate),
                    getLocalDateFromUTC(filters.endDate),
                  ]
                : []
            }
            options={{
              mode: "range",
              dateFormat: "m-d-Y",
            }}
            placeholder="Select custom date range"
            onChange={handleCustomDateChange}
            onClose={handleFlatpickrClose}
          />
          <CalendarIcon />
        </div>
      )}
    </div>
  );

  const renderGenderField = () => (
    <div className="analytics-filter-field">
      <label>Gender</label>
      <Select
        isMulti
        closeMenuOnSelect={false}
        classNamePrefix="analytics-select"
        options={genderOptions}
        value={genderOptions.filter((opt) =>
          (filters.genders || []).includes(opt.value)
        )}
        onChange={(selected) => handleMultiChange("genders", selected)}
        placeholder="All Genders"
      />
    </div>
  );

  const renderAssessmentField = () => (
    <div className="analytics-filter-field">
      <label>Assessment Type</label>
      <Select
        isMulti
        closeMenuOnSelect={false}
        classNamePrefix="analytics-select"
        options={assessmentTypeOptions}
        value={assessmentTypeOptions.filter((opt) =>
          (filters.assessmentTypes || []).includes(opt.value)
        )}
        onChange={(selected) => handleMultiChange("assessmentTypes", selected)}
        placeholder="All Types"
      />
    </div>
  );

  const renderFilterActions = () => (
    <div className="analytics-filter-actions">
      <button
        type="button"
        className={`analytics-btn-reset${loadingAction === "reset" ? " analytics-btn-loading" : ""}`}
        onClick={handleReset}
        disabled={isDataLoading}
      >
        {loadingAction === "reset" ? (
          <>
            <span className="analytics-btn-spinner" />
            Resetting...
          </>
        ) : (
          "Reset All Filters"
        )}
      </button>
      <button
        type="button"
        className={`analytics-btn-apply${loadingAction === "apply" ? " analytics-btn-loading" : ""}`}
        onClick={handleApply}
        disabled={isDataLoading}
      >
        {loadingAction === "apply" ? (
          <>
            <span className="analytics-btn-spinner analytics-btn-spinner--apply" />
            Applying...
          </>
        ) : (
          "Apply Filter"
        )}
      </button>
    </div>
  );

  return (
    <div className="analytics-filters">
      <div
        className={`analytics-filters-grid${!isPatientLevel ? " analytics-filters-grid--4col" : ""}`}
      >
        <div className="analytics-filter-field">
          <label>Clinic Name</label>
          <Select
            isMulti
            closeMenuOnSelect={false}
            classNamePrefix="analytics-select"
            options={clinicOptions}
            value={clinicOptions.filter((opt) =>
              (filters.clinicIds || []).includes(opt.value)
            )}
            onChange={(selected) => handleMultiChange("clinicIds", selected)}
            isDisabled={clinicListLoading}
            placeholder={
              clinicListLoading ? "Loading clinics..." : "All Clinics"
            }
          />
        </div>
        <div className="analytics-filter-field">
          <label>Patient Name</label>
          {hasGroupedPatientShape && renderPatientCategoryRadios()}
          <Select
            isMulti
            closeMenuOnSelect={false}
            classNamePrefix="analytics-select"
            options={visiblePatientOptions}
            getOptionValue={(opt) =>
              `${opt.value.type}_${opt.value.patient_id}_${opt.value.healthie_patient_id}`
            }
            value={flatPatientOptions.filter((opt) =>
              (filters.patientIds || []).some(
                (pid) =>
                  pid.type === opt.value.type &&
                  pid.patient_id === opt.value.patient_id &&
                  pid.healthie_patient_id === opt.value.healthie_patient_id
              )
            )}
            onChange={(selected) => handleMultiChange("patientIds", selected)}
            isDisabled={
              patientListLoading ||
              (hasGroupedPatientShape && !patientCategory)
            }
            placeholder={
              patientListLoading
                ? "Loading patients..."
                : hasGroupedPatientShape && !patientCategory
                  ? "Select a category first"
                  : PATIENT_CATEGORY_OPTIONS.find(
                      (opt) => opt.value === patientCategory
                    )?.label || "All Patients"
            }
          />
        </div>
        {isPatientLevel ? renderGenderField() : renderAssessmentField()}
        {!isPatientLevel && renderAgeRangeField()}
      </div>

      <div className="analytics-filters-grid analytics-filters-grid--4col analytics-filters-grid--last">
        {isPatientLevel ? renderAgeRangeField() : renderGenderField()}
        {renderTimeRangeField()}
        {renderTimeInCareField()}
        <div className="analytics-filter-field analytics-filter-field--actions">
          {renderFilterActions()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilters;
